import { Button } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useEffect, type Dispatch, type SetStateAction } from 'react';
import type {
    SupplierRequestComingTransferDest,
    SupplierRequestComingType,
    WarehouseComingRowType,
} from '@econtractor/types';
import spinnerTitle from 'components/others/spinnerTitle';
import { FilesCloudUpload } from 'modules/files';
import { ComingTable } from 'modules/warehouse';
import ComingBatches from '../ComingBatches';
import ComingDestSelect from '../ComingDestSelect';
import ComingObjectSelect from '../ComingObjectSelect';
import SolarIcon from 'components/icons/SolarIcon';
import type { ComingDest, ComingObjectOption } from '../../model/comingObject';
import { comingMatchesObjectFilter } from '../../model/comingObject';

interface TabComingProps {
    canStartComing: boolean;
    showDraft: boolean;
    draftComing: WarehouseComingRowType[];
    setDraftComing: Dispatch<SetStateAction<WarehouseComingRowType[]>>;
    isSubmitting: boolean;
    canCredit: boolean;
    pendingCloudFiles: File[];
    setPendingCloudFiles: Dispatch<SetStateAction<File[]>>;
    cloudProgress: number | boolean;
    getMaxCount: (row: WarehouseComingRowType) => number | null;
    onOpenDraft: () => void;
    onCancelDraft: () => void;
    onConduct: () => void;
    comings: SupplierRequestComingType[];
    objectOptions: ComingObjectOption[];
    selectedObjectKey: number;
    onSelectObject: (key: number) => void;
    comingDest: ComingDest;
    destOptions: ComingDest[];
    selectedObjectOption: ComingObjectOption | null;
    onSelectDest: (dest: ComingDest) => void;
    canOps: boolean;
    formBusy: boolean;
    transferringComingId: number | null;
    onTransferComing: (
        coming: SupplierRequestComingType,
        dest: SupplierRequestComingTransferDest,
    ) => void;
}

const TabComing = ({
    canStartComing,
    showDraft,
    draftComing,
    setDraftComing,
    isSubmitting,
    canCredit,
    pendingCloudFiles,
    setPendingCloudFiles,
    cloudProgress,
    getMaxCount,
    onOpenDraft,
    onCancelDraft,
    onConduct,
    comings,
    objectOptions,
    selectedObjectKey,
    onSelectObject,
    comingDest,
    destOptions,
    selectedObjectOption,
    onSelectDest,
    canOps,
    formBusy,
    transferringComingId,
    onTransferComing,
}: TabComingProps) => {
    const { t } = useTranslation(['Vendors', 'common']);
    const formDisabled = formBusy;
    const hasPaidRows =
        canCredit && draftComing.some((row) => Boolean(row?.coming?.payment));
    const selectedComings = comings.filter((coming) =>
        comingMatchesObjectFilter(coming.objectId, selectedObjectKey),
    );

    useEffect(() => {
        if (!hasPaidRows && pendingCloudFiles.length) {
            setPendingCloudFiles([]);
        }
    }, [hasPaidRows, pendingCloudFiles.length, setPendingCloudFiles]);

    return (
        <>
            <ComingObjectSelect
                options={objectOptions}
                selectedKey={selectedObjectKey}
                disabled={showDraft || formDisabled}
                onSelect={onSelectObject}
            />
            <ComingDestSelect
                dest={comingDest}
                destOptions={destOptions}
                selectedObject={selectedObjectOption}
                disabled={formDisabled}
                onSelect={onSelectDest}
            />

            <div className="mb-3">
                {canStartComing && !showDraft ? (
                    <Button
                        size="sm"
                        variant="info"
                        disabled={formDisabled}
                        onClick={(e) => {
                            e.currentTarget.blur();
                            onOpenDraft();
                        }}
                    >
                        <SolarIcon name="box-arrow-in-down" className="me-1" />
                        {t('supplierRequests.view.btnFormComing')}
                    </Button>
                ) : null}

                {!canStartComing && !showDraft && !selectedComings.length ? (
                    <p className="text-muted small mb-0">
                        {t('supplierRequests.view.comingEmpty')}
                    </p>
                ) : null}
            </div>

            {showDraft ? (
                <div className="mb-3">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                        <h6 className="mb-0 text-muted">
                            {t('supplierRequests.view.draftTitle')}
                        </h6>
                        <Button
                            size="sm"
                            variant="link"
                            className="p-0"
                            disabled={formDisabled}
                            onClick={onCancelDraft}
                        >
                            {t('common:buttons.cancel')}
                        </Button>
                    </div>
                    <div className="mx-n3">
                        <ComingTable
                            aComing={draftComing}
                            setAComing={setDraftComing}
                            comingProcess={formDisabled}
                            showPayment={canCredit}
                            getMaxCount={getMaxCount}
                        />
                    </div>
                    {hasPaidRows ? (
                        <div className="mt-3">
                            <FilesCloudUpload
                                disabled={formDisabled}
                                progress={cloudProgress}
                                files={pendingCloudFiles}
                                setFiles={setPendingCloudFiles}
                                label={t('common:labelAttachments')}
                                buttonLabel={t('common:buttons.upload')}
                                uploadingLabel={t('common:buttons.uploading')}
                            />
                        </div>
                    ) : null}
                    <div className="d-flex justify-content-end mt-2">
                        <Button
                            variant="info"
                            className="min100px"
                            disabled={formDisabled || !draftComing.length}
                            onClick={() => void onConduct()}
                        >
                            {spinnerTitle(
                                <>
                                    <SolarIcon name="box-arrow-in-down" className="fs-14 me-2" />
                                    {t('supplierRequests.view.btnApproved')}
                                </>,
                                isSubmitting || cloudProgress !== false,
                                t('common:buttons.saving'),
                            )}
                        </Button>
                    </div>
                </div>
            ) : null}

            <ComingBatches
                comings={comings}
                selectedObjectKey={selectedObjectKey}
                canOps={canOps}
                disabled={formDisabled}
                transferringComingId={transferringComingId}
                onTransfer={onTransferComing}
            />
        </>
    );
};

export default TabComing;
